<template>
	<VueApexChart
		v-if="chartOptions"
		ref="chart"
		class="app-chart-line"
		:class="{ 'live' : autoUpdate }"
		width="100%"
		height="100%"
		:options="chartOptions"
		:series="series"
	/>
</template>

<script>
export default {
	props: {
		strokeColor: {
			type: String,
			default: '#54D8FF'
		},

		// define the width of y-axis labels
		yAxisWidth: {
			type: Number,
			default: 60
		},

		labels: {
			type: Array,
			default: () => []
		},

		series: {
			type: Array,
			default: () => []
		},

		autoUpdate: {
			type: Boolean,
			default: false
		},

		// define the visible minutes
		xAxisRange: {
			type: Number,
			default: 60
		},

		// define minimum value for x-axis if no data is provided
		xMinValue: {
			type: [String, Date],
			default: () => new Date()
		},

		toolbar: {
			type: Boolean,
			default: true
		},

		formatTooltip: {
			type: Boolean,
			default: true
		},

		title: String,
		tooltipTitle: String,
		noData: {
			type: String,
			default: 'No Data'
		}
	},

	watch: {
		labels (newVal, oldVal) {
			if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
				this.init()
			}
		}
	},

	async created () {
		// add watcher for render related props
		this.$watch(vm => [
			vm.yAxisWidth,
			vm.autoUpdate,
			vm.xAxisRange,
			vm.xMinValue,
			vm.toolbar,
			vm.formatTooltip,
			vm.title,
			vm.tooltipTitle,
			vm.noData
		], async () => {
			this.chartOptions = null
			await this.init()
			if (!this.autoUpdate) { this.setDefaultZoom() }
		})

		await this.init()
		if (!this.autoUpdate) {
			this.setDefaultZoom()
		}
	},

	data () {
		return {
			chartOptions: null
		}
	},

	computed: {
		rangeInMinutes () {
			return this.xAxisRange * 60000
		}
	},

	methods: {
		getXTick () {
			if (this.series.length && this.series[0].data && this.series[0].data.length) {
				const data = this.series[0].data
				const min = new Date(data[0][0]).getTime()
				const max = new Date(data[data.length - 1][0]).getTime()
				const diff = max - min
				const isBased = diff <= this.rangeInMinutes
				return {
					min: isBased ? min : max - this.rangeInMinutes,
					max: max
				}
			}

			return {
				min: this.xMinValue.getTime(),
				max: this.xMinValue.getTime() + this.rangeInMinutes
			}
		},

		getYTick () {
			const length = this.labels.length
			if (length) {
				if (typeof this.labels[0] === 'string') {
					return {
						tickAmount: length - 1,
						min: 0,
						max: length - 1
					}
				}
				if (typeof this.labels[0] === 'number') {
					return {
						tickAmount: length - 1,
						min: 0,
						max: this.labels[length - 1]
					}
				}
			}

			return {
				tickAmount: undefined,
				min: 0,
				max: undefined
			}
		},

		async init () {
			// const xTtick = await this.getXTick()
			const yTtick = await this.getYTick()

			this.chartOptions = {
				chart: {
					type: 'line',
					toolbar: {
						show: !this.autoUpdate && this.toolbar,
						tools: {
							download: false,
							reset: false,
							customIcons: [{
								icon: '<span style="font-size: 16px; margin-left: 5px;">&times;</span>',
								index: 4,
								title: 'Reset',
								class: 'custom-icon',
								click: () => {
									this.setDefaultZoom()
								}
							}]
						},
						autoSelected: 'pan'
					},
					animations: {
						easing: 'linear',
						speed: 500,
						animateGradually: { delay: 500 },
						dynamicAnimation: { speed: 500 }
					}
				},
				title: {
					text: this.title,
					margin: 0,
					style: {
						color: 'currentColor',
						fontWeight: 'normal',
						fontSize: '11px'
					}
				},
				dataLabels: { enabled: false },
				stroke: {
					width: 2,
					curve: 'smooth',
					colors: this.strokeColor
				},
				markers: {
					size: 2,
					colors: [this.strokeColor],
					strokeColor: this.strokeColor,
					hover: { sizeOffset: 1 }
				},
				grid: {
					background: '#fff',
					borderColor: '#eee',
					padding: { left: 15, bottom: 15 }
				},

				tooltip: {
					enabled: !this.autoUpdate,
					fixed: { enabled: true, offsetY: -20 },
					style: { fontSize: '12px' },
					marker: { show: false },
					x: {
						show: true,
						format: 'MMM dd, yyyy hh:mm TT'
					},
					y: {
						formatter: (val) => {
							if (!this.formatTooltip || !this.labels.length) return val
							return this.labels[val]
						},
						title: { formatter: () => this.tooltipTitle }
					}
				},
				xaxis: {
					type: 'datetime',
					// min: xTtick.min,
					// max: xTtick.max,
					// range: this.rangeInMinutes,
					labels: {
						datetimeUTC: false, // true to use UTC
						datetimeFormatter: {
							hour: 'h TT',
							minute: 'h:mm TT',
							second: 'h:mm:00 TT'
						},
						style: { fontSize: '10px' }
					},
					tooltip: { enabled: false }
				},
				yaxis: {
					tickAmount: yTtick.tickAmount,
					min: yTtick.min,
					max: yTtick.max,
					labels: {
						formatter: (value, index) => this.labels.length ? this.labels[index] : value,
						style: { fontSize: '10px' },
						minWidth: this.yAxisWidth
					}
				},
				noData: {
					text: this.noData,
					align: 'center',
					verticalAlign: 'middle',
					style: { fontSize: '10px' }
				}
			}
		},

		setDefaultZoom () {
			if (!this.series.length || !this.series[0].data.length) return
			const seriesData = this.series[0].data
			const lastData = new Date(seriesData[seriesData.length - 1][0]).getTime()
			const startData = new Date(seriesData[0][0]).getTime()
			const diff = lastData - startData
			const isBased = diff <= this.rangeInMinutes
			const end = lastData
			const start = isBased ? startData : end - this.rangeInMinutes
			this.$refs.chart.zoomX(start, end)
		}
	}
}
</script>

<style lang="scss" scoped>
.app-chart-line {
	width: 100%;
	height: 100%;
	min-height: 0;
	min-width: 0;
	text-transform: capitalize;
	position: relative;

	&.live { pointer-events: none; }
}
</style>
